/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/UsersTable/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import styles from "./UsersTable.module.css";

import { ReactComponent as ActiveIcon } from "../../assets/icons/UsersTable/ActiveIcon.svg";
import { ReactComponent as InactiveIcon } from "../../assets/icons/UsersTable/InactiveIcon.svg";

import { useTranslation } from "react-i18next";
import AssetImage from "../AssetImage";

const UsersTable = ({
  users,
  sortConfig,
  onSort,
  onDisplayClick,
  onEditClick,
  onTabletCardClick,
  onMobileCardClick,
  renderArrow,
  currentUser,
  companies,
}) => {
  const { t } = useTranslation();

  // Helper function to get company name
  const getCompanyName = (user) => {
    if (user.RFC && companies[user.RFC]) {
      return companies[user.RFC].nombreComercial || t("common.defaultCompanyName");
    }
    return t("common.defaultCompanyName");
  };

  return (
    <div className={styles.usersTableContainer}>
      {/* Desktop/Laptop Table View */}
      <div className={styles.desktopView}>
        <table className={styles.usersTable}>
          <thead>
            <tr>
              <th className={styles.userRoleHeader}>
                {t("components.usersTable.role")}
              </th>
              <th onClick={onSort} className={styles.userNameHeader}>
                <div className={styles.sortableHeader}>
                  {t("components.usersTable.name")} {renderArrow()}
                </div>
              </th>
              <th className={styles.companyHeader}>
                {t("components.usersTable.company")}
              </th>
              <th className={styles.actionsHeader}>
                {t("components.usersTable.actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <tr
                key={index}
                className={!user.activo ? styles.inactiveRow : ""}
              >
                <td className={`${styles.userRole} ${styles.userRoleData}`}>
                  {user.nombreRol && (
                    <span className={styles.userRoleBadge}>
                      {user.nombreRol}
                    </span>
                  )}
                </td>
                <td className={`${styles.userName} ${styles.userNameData}`}>
                  <div className={styles.userNameContainer}>
                    {user.imagenDePerfil && (
                      <AssetImage
                        src={user.imagenDePerfil}
                        alt={t("common.userProfileAlt", { name: user.nombre })}
                        className={styles.userProfileImage}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    {user.activo ? (
                      <ActiveIcon className={styles.statusIcon} />
                    ) : (
                      <InactiveIcon className={styles.statusIcon} />
                    )}
                    <div className={styles.userInfoContainer}>
                      <span className={styles.userNameText}>{user.nombre}</span>
                      {user.correoElectronico && (
                        <span className={styles.userEmail}>
                          {user.correoElectronico}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className={styles.companyData}>
                  <div className={styles.companyInfoContainer}>
                    <span className={styles.companyName}>
                      {getCompanyName(user)}
                    </span>
                    {(user.numeroSocio || user.RFC) && (
                      <span className={styles.companyDetails}>
                        {user.numeroSocio && <span>{user.numeroSocio}</span>}
                        {user.numeroSocio && user.RFC && (
                          <span className={styles.separator}>/</span>
                        )}
                        {user.RFC && <span>{user.RFC}</span>}
                      </span>
                    )}
                  </div>
                </td>
                <td className={styles.actionsData}>
                  <VisibilityIcon
                    className={styles.usersTableIcon}
                    onClick={() => onDisplayClick(user)}
                  />
                  {currentUser?.tipoDeUsuario !== "3" &&
                    currentUser?.tipoDeUsuario !== "4" &&
                    currentUser?.tipoDeUsuario !== "12" && (
                      <EditIcon
                        className={styles.usersTableIcon}
                        onClick={() => onEditClick(user)}
                      />
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tablet Card View */}
      <div className={styles.tabletView}>
        {users.map((user, index) => (
          <div
            key={index}
            className={`${styles.tabletCard} ${
              !user.activo ? styles.inactiveRow : ""
            }`}
            onClick={() => onTabletCardClick && onTabletCardClick(user)}
          >
            {/* Column 1: Profile Image + Role */}
            <div className={styles.tabletColumn1}>
              {user.imagenDePerfil && (
                  <AssetImage
                  src={user.imagenDePerfil}
                  alt={t("common.userProfileAlt", { name: user.nombre })}
                  className={styles.tabletProfileImage}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              {user.nombreRol && (
                <span className={styles.tabletRoleBadge}>{user.nombreRol}</span>
              )}
            </div>

            {/* Column 2: User Info */}
            <div className={styles.tabletColumn2}>
              <div className={styles.tabletUserInfo}>
                <div className={styles.tabletNameRow}>
                  {user.activo ? (
                    <ActiveIcon className={styles.tabletStatusIcon} />
                  ) : (
                    <InactiveIcon className={styles.tabletStatusIcon} />
                  )}
                  <span className={styles.tabletUserName}>{user.nombre}</span>
                </div>
                <span className={styles.tabletCompanyName}>
                  {getCompanyName(user)}
                </span>
                {(user.numeroSocio || user.RFC) && (
                  <span className={styles.tabletCompanyDetails}>
                    {user.numeroSocio && <span>{user.numeroSocio}</span>}
                    {user.numeroSocio && user.RFC && (
                      <span className={styles.tabletSeparator}>/</span>
                    )}
                    {user.RFC && <span>{user.RFC}</span>}
                  </span>
                )}
              </div>
            </div>

            {/* Column 3: Edit Action */}
            <div className={styles.tabletColumn3}>
              {currentUser?.tipoDeUsuario !== "3" &&
                currentUser?.tipoDeUsuario !== "4" &&
                currentUser?.tipoDeUsuario !== "12" && (
                  <EditIcon
                    className={styles.tabletEditIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditClick(user);
                    }}
                  />
                )}
            </div>
          </div>
        ))}
      </div>

      {/* Medium Mobile Card View */}
      <div className={styles.mediumMobileView}>
        {users.map((user, index) => (
          <div
            key={index}
            className={`${styles.mediumMobileCard} ${
              !user.activo ? styles.inactiveRow : ""
            }`}
            onClick={() => onMobileCardClick && onMobileCardClick(user)}
          >
            {/* Profile Image */}
            <div className={styles.mediumMobileProfileSection}>
              {user.imagenDePerfil && (
                  <AssetImage
                  src={user.imagenDePerfil}
                  alt={t("common.userProfileAlt", { name: user.nombre })}
                  className={styles.mediumMobileProfileImage}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              {user.activo ? (
                <ActiveIcon className={styles.mediumMobileStatusIcon} />
              ) : (
                <InactiveIcon className={styles.mediumMobileStatusIcon} />
              )}
            </div>

            {/* User Info */}
            <div className={styles.mediumMobileInfoSection}>
              <div className={styles.mediumMobileNameRow}>
                <span className={styles.mediumMobileUserName}>
                  {user.nombre}
                </span>
                {user.nombreRol && (
                  <span className={styles.mediumMobileRoleBadge}>
                    {user.nombreRol}
                  </span>
                )}
              </div>
              <span className={styles.mediumMobileCompanyName}>
                {getCompanyName(user)}
              </span>
              {(user.numeroSocio || user.RFC) && (
                <span className={styles.mediumMobileCompanyDetails}>
                  {user.numeroSocio && <span>{user.numeroSocio}</span>}
                  {user.numeroSocio && user.RFC && (
                    <span className={styles.mediumMobileSeparator}>/</span>
                  )}
                  {user.RFC && <span>{user.RFC}</span>}
                </span>
              )}
            </div>

            {/* Edit Action */}
            <div className={styles.mediumMobileActionSection}>
              {currentUser?.tipoDeUsuario !== "3" &&
                currentUser?.tipoDeUsuario !== "4" &&
                currentUser?.tipoDeUsuario !== "12" && (
                  <EditIcon
                    className={styles.mediumMobileEditIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditClick(user);
                    }}
                  />
                )}
            </div>
          </div>
        ))}
      </div>

      {/* Small Mobile Card View */}
      <div className={styles.smallMobileView}>
        {users.map((user, index) => (
          <div
            key={index}
            className={`${styles.smallMobileCard} ${
              !user.activo ? styles.inactiveRow : ""
            }`}
            onClick={() => onMobileCardClick && onMobileCardClick(user)}
          >
            {/* Left Column: Avatar and Role Badge */}
            <div className={styles.smallMobileLeftColumn}>
              {user.imagenDePerfil && (
                  <AssetImage
                  src={user.imagenDePerfil}
                  alt={t("common.userProfileAlt", { name: user.nombre })}
                  className={styles.smallMobileProfileImage}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              {user.nombreRol && (
                <span className={styles.smallMobileRoleBadge}>
                  {user.nombreRol}
                </span>
              )}
            </div>

            {/* Right Column: User Info and Actions */}
            <div className={styles.smallMobileRightColumn}>
              {/* Edit Icon */}
              <div className={styles.smallMobileEditContainer}>
                {currentUser?.tipoDeUsuario !== "3" &&
                  currentUser?.tipoDeUsuario !== "4" &&
                  currentUser?.tipoDeUsuario !== "12" && (
                    <EditIcon
                      className={styles.smallMobileEditIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditClick(user);
                      }}
                    />
                  )}
              </div>

              {/* User Info */}
              <div className={styles.smallMobileInfoSection}>
                <div className={styles.smallMobileNameRow}>
                  {user.activo ? (
                    <ActiveIcon className={styles.smallMobileStatusIcon} />
                  ) : (
                    <InactiveIcon className={styles.smallMobileStatusIcon} />
                  )}
                  <span className={styles.smallMobileUserName}>
                    {user.nombre}
                  </span>
                </div>
                <span className={styles.smallMobileCompanyName}>
                  {getCompanyName(user)}
                </span>
                {(user.numeroSocio || user.RFC) && (
                  <span className={styles.smallMobileCompanyDetails}>
                    {user.numeroSocio && <span>{user.numeroSocio}</span>}
                    {user.numeroSocio && user.RFC && (
                      <span className={styles.smallMobileSeparator}>/</span>
                    )}
                    {user.RFC && <span>{user.RFC}</span>}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsersTable;
